/**
 * Creator v2 ↔ the formats that already exist.
 *
 * The puzzle doc does not change shape (constraint C1): the new creator produces
 * exactly the config `createPuzzle` accepts from the old one. The recipient's
 * name and the flow position live only in the draft.
 */
import { initialState, isClueComplete, emptyClue } from "./state";
import { LIMITS, CELEBRATIONS } from "./constants";

const trim = (s) => String(s ?? "").trim();

function toLevel(clue, index) {
  const type = CELEBRATIONS.includes(clue.celebrationType) ? clue.celebrationType : "default";
  return {
    id: index + 1,
    title: trim(clue.title).slice(0, LIMITS.clueTitle),
    question: trim(clue.question),
    answer: trim(clue.answer),
    hint: trim(clue.hint),
    celebrationType: type,
    celebrationContent: type === "default" ? "" : String(clue.celebrationContent || ""),
  };
}

export function titleFor(state, defaultTitle) {
  return (trim(state.title) || trim(defaultTitle)).slice(0, LIMITS.title);
}

/**
 * The config handed to createPuzzle (via ShareScreen). Only complete clues are
 * published, renumbered in their current order.
 * @param {object} state creator state
 * @param {{ defaultTitle: string }} opts localized fallback title ("For Roberto")
 */
export function toPuzzleConfig(state, { defaultTitle }) {
  const sealed = !!state.seal.enabled;
  const config = {
    version: 1,
    _puzzleId: state.puzzleId,
    theme: state.theme,
    title: titleFor(state, defaultTitle),
    // "" means unsealed (see isSealed in puzzleService). Lower-cased like the old
    // creator did; the hash lower-cases too.
    password: sealed ? trim(state.seal.word).toLowerCase() : "",
    passwordHint: sealed ? trim(state.seal.hint) : "",
    finalMessage: trim(state.finalMessage),
    levels: state.clues.filter(isClueComplete).map(toLevel),
  };
  if (state.finale && state.finale.url) config.finale = { type: state.finale.type, url: state.finale.url };
  return config;
}

/**
 * The config saved to drafts/{uid}. Keeps unfinished clues and the flow
 * position so a resumed draft lands where the user left.
 */
export function toDraftConfig(state, { defaultTitle }) {
  const base = toPuzzleConfig(state, { defaultTitle });
  return {
    ...base,
    levels: state.clues.map(toLevel),
    _creator: {
      version: 2,
      recipientName: state.recipientName,
      step: state.step === "share" ? "ready" : state.step,
      clueIndex: state.clueIndex,
      returnTo: state.returnTo,
      sealEnabled: !!state.seal.enabled,
      word: state.seal.word,
      title: state.title,
    },
  };
}

/** A draft can only be saved once there is a theme and at least one clue (Rules). */
// A draft needs a vibe and at least one clue with something written in it (the
// drafts Rules require a theme and levels). A clue opened and left empty does
// not count: there is one draft per user, so saving it would replace a real one.
export function isDraftable(state) {
  const hasWriting = (c) => String(c.question || "").trim() !== "" || String(c.answer || "").trim() !== "";
  return !!state.theme && state.clues.some(hasWriting);
}

function clueFromLevel(level) {
  return {
    ...emptyClue(),
    question: String(level.question || ""),
    answer: String(level.answer || ""),
    hint: String(level.hint || ""),
    title: String(level.title || ""),
    celebrationType: CELEBRATIONS.includes(level.celebrationType) ? level.celebrationType : "default",
    celebrationContent: String(level.celebrationContent || ""),
  };
}

/**
 * Rebuild creator state from a saved draft config — ours (v2), the old wizard's,
 * or Quick Puzzle's. Old drafts have no recipient name or flow position; they
 * open on the clue list, the one screen that shows the whole puzzle.
 * @param {object} config drafts/{uid}.config
 * @param {{ newPuzzleId: () => string }} deps
 */
export function fromDraftConfig(config, { newPuzzleId }) {
  const puzzleId = /^[A-Za-z0-9]{12}$/.test(config?._puzzleId || "") ? config._puzzleId : newPuzzleId();
  const meta = config?._creator && config._creator.version === 2 ? config._creator : null;
  const levels = Array.isArray(config?.levels) ? config.levels : [];
  const clues = levels.map(clueFromLevel);

  const state = {
    ...initialState({ puzzleId, recipientName: meta ? meta.recipientName || "" : "", theme: config?.theme || null }),
    clues,
    finalMessage: String(config?.finalMessage || ""),
    finale: config?.finale && config.finale.url ? { type: config.finale.type, url: config.finale.url } : null,
  };

  if (meta) {
    return {
      ...state,
      step: meta.step || "list",
      clueIndex: Math.min(Math.max(0, meta.clueIndex || 0), Math.max(0, clues.length - 1)),
      returnTo: meta.returnTo || "list",
      seal: { enabled: meta.sealEnabled !== false, word: String(meta.word || ""), hint: String(config.passwordHint || "") },
      title: String(meta.title || ""),
    };
  }

  // Old wizard / Quick Puzzle draft: the password was stored in plain text.
  const word = String(config?.password || "");
  return {
    ...state,
    step: clues.length ? "list" : "vibe",
    returnTo: "list",
    seal: { enabled: word.trim() !== "", word, hint: String(config?.passwordHint || "") },
    title: String(config?.title || ""),
  };
}
